import React, { useState, useRef } from 'react';
import './PositionDebugPanel.css';

const TEST_WALLETS = [
  '0xTEST000000000000000000000000000000000001',
  '0xTEST000000000000000000000000000000000002',
  '0xTEST000000000000000000000000000000000003',
  '0xTEST000000000000000000000000000000000004',
  '0xTEST000000000000000000000000000000000005',
];

let walletCounter = 0;
function getNextWallet() {
  const wallet = TEST_WALLETS[walletCounter % TEST_WALLETS.length];
  walletCounter++;
  return wallet;
}

function PositionDebugPanel({ onSimulatePositionOpened, onSimulatePositionUpdate, onSimulatePositionClosed, isConnected, isSubscribed }) {
  const [form, setForm] = useState({
    symbol: 'BTC',
    side: 'long',
    positionValue: 5000000,
    entryPrice: 95000,
    markPrice: 96000,
    leverage: 10,
    unrealizedPnl: 50000,
    returnOnEquity: 0.105,
    liquidationPrice: 85000,
  });

  // Track simulated positions for update/close
  const simulatedRef = useRef([]);

  const buildPosition = (overrides = {}) => {
    const wallet = getNextWallet();
    const base = {
      user: wallet,
      symbol: form.symbol,
      side: form.side,
      size: form.positionValue / form.entryPrice,
      entryPrice: form.entryPrice,
      markPrice: form.markPrice,
      leverage: form.leverage,
      positionValue: form.positionValue,
      unrealizedPnl: form.unrealizedPnl,
      returnOnEquity: form.returnOnEquity,
      liquidationPrice: form.liquidationPrice,
      marginUsed: form.positionValue / form.leverage,
      isSimulated: true,
    };
    return { ...base, ...overrides };
  };

  const handleOpenPosition = (customData = null) => {
    const position = customData || buildPosition();
    simulatedRef.current.push({ user: position.user, symbol: position.symbol });
    onSimulatePositionOpened(position);
  };

  const handleUpdateLastPnl = () => {
    if (simulatedRef.current.length === 0) return;
    const last = simulatedRef.current[simulatedRef.current.length - 1];
    const newPnl = Math.round((Math.random() - 0.3) * 200000);
    onSimulatePositionUpdate({
      ...last,
      side: form.side,
      size: form.positionValue / form.entryPrice,
      entryPrice: form.entryPrice,
      markPrice: form.markPrice + (newPnl > 0 ? 500 : -500),
      leverage: form.leverage,
      positionValue: form.positionValue,
      unrealizedPnl: newPnl,
      returnOnEquity: newPnl / (form.positionValue / form.leverage),
      liquidationPrice: form.liquidationPrice,
      marginUsed: form.positionValue / form.leverage,
      symbol: last.symbol,
      sizeChange: 0,
    });
  };

  const handleCloseLastPosition = () => {
    if (simulatedRef.current.length === 0) return;
    const last = simulatedRef.current.pop();
    onSimulatePositionClosed(last);
  };

  const handleCloseAllSimulated = () => {
    simulatedRef.current.forEach(pos => onSimulatePositionClosed(pos));
    simulatedRef.current = [];
  };

  const handlePreset = (side, positionValue, symbol = 'BTC') => {
    const entryPrice = symbol === 'BTC' ? 95000 : symbol === 'ETH' ? 3200 : symbol === 'SOL' ? 150 : 95000;
    const leverage = 10;
    const pnlPercent = side === 'long' ? 0.08 : -0.05;
    const marginUsed = positionValue / leverage;
    const unrealizedPnl = Math.round(marginUsed * pnlPercent);
    const markPrice = side === 'long'
      ? entryPrice * (1 + pnlPercent / leverage)
      : entryPrice * (1 - pnlPercent / leverage);
    const liquidationPrice = side === 'long'
      ? entryPrice * (1 - 0.9 / leverage)
      : entryPrice * (1 + 0.9 / leverage);

    const wallet = getNextWallet();
    const position = {
      user: wallet,
      symbol,
      side,
      size: positionValue / entryPrice,
      entryPrice,
      markPrice: Math.round(markPrice * 100) / 100,
      leverage,
      positionValue,
      unrealizedPnl,
      returnOnEquity: pnlPercent,
      liquidationPrice: Math.round(liquidationPrice * 100) / 100,
      marginUsed,
      isSimulated: true,
    };

    simulatedRef.current.push({ user: wallet, symbol });
    onSimulatePositionOpened(position);
  };

  return (
    <div className="position-debug-panel">
      <div className="debug-header">
        <h3>Position Debug Panel</h3>
        <div className="debug-status">
          <span className={`status-indicator ${isConnected ? 'connected' : 'disconnected'}`}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          <span className={`status-indicator ${isSubscribed ? 'subscribed' : 'unsubscribed'}`}>
            {isSubscribed ? 'Subscribed' : 'Not Subscribed'}
          </span>
        </div>
      </div>

      {/* Tier Presets */}
      <div className="debug-section">
        <h4>Tier Presets</h4>

        <div className="preset-tier-group">
          <h5>Small ($500K)</h5>
          <div className="button-grid">
            <button onClick={() => handlePreset('long', 500000, 'BTC')} className="debug-btn preset-btn tier-small long">LONG</button>
            <button onClick={() => handlePreset('short', 500000, 'BTC')} className="debug-btn preset-btn tier-small short">SHORT</button>
          </div>
        </div>

        <div className="preset-tier-group">
          <h5>Medium ($5M)</h5>
          <div className="button-grid">
            <button onClick={() => handlePreset('long', 5000000, 'ETH')} className="debug-btn preset-btn tier-medium long">LONG</button>
            <button onClick={() => handlePreset('short', 5000000, 'ETH')} className="debug-btn preset-btn tier-medium short">SHORT</button>
          </div>
        </div>

        <div className="preset-tier-group">
          <h5>Large ($25M)</h5>
          <div className="button-grid">
            <button onClick={() => handlePreset('long', 25000000, 'BTC')} className="debug-btn preset-btn tier-large long">LONG</button>
            <button onClick={() => handlePreset('short', 25000000, 'BTC')} className="debug-btn preset-btn tier-large short">SHORT</button>
          </div>
        </div>

        <div className="preset-tier-group">
          <h5>Tier 3 ($75M) - pulse</h5>
          <div className="button-grid">
            <button onClick={() => handlePreset('long', 75000000, 'BTC')} className="debug-btn preset-btn tier3 long">LONG</button>
            <button onClick={() => handlePreset('short', 75000000, 'BTC')} className="debug-btn preset-btn tier3 short">SHORT</button>
          </div>
        </div>

        <div className="preset-tier-group">
          <h5>Tier 4 ($150M) - ultra pulse</h5>
          <div className="button-grid">
            <button onClick={() => handlePreset('long', 150000000, 'BTC')} className="debug-btn preset-btn tier4 long">LONG</button>
            <button onClick={() => handlePreset('short', 150000000, 'BTC')} className="debug-btn preset-btn tier4 short">SHORT</button>
          </div>
        </div>
      </div>

      {/* Manual Form */}
      <div className="debug-section">
        <h4>Manual Position</h4>
        <div className="form-grid">
          <div className="form-group">
            <label>Symbol</label>
            <input type="text" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })} />
          </div>
          <div className="form-group">
            <label>Side</label>
            <select value={form.side} onChange={(e) => setForm({ ...form, side: e.target.value })}>
              <option value="long">Long</option>
              <option value="short">Short</option>
            </select>
          </div>
          <div className="form-group">
            <label>Position Value ($)</label>
            <input type="number" value={form.positionValue} onChange={(e) => setForm({ ...form, positionValue: Number(e.target.value) })} step="100000" />
          </div>
          <div className="form-group">
            <label>Entry Price ($)</label>
            <input type="number" value={form.entryPrice} onChange={(e) => setForm({ ...form, entryPrice: Number(e.target.value) })} step="0.01" />
          </div>
          <div className="form-group">
            <label>Mark Price ($)</label>
            <input type="number" value={form.markPrice} onChange={(e) => setForm({ ...form, markPrice: Number(e.target.value) })} step="0.01" />
          </div>
          <div className="form-group">
            <label>Leverage</label>
            <input type="number" value={form.leverage} onChange={(e) => setForm({ ...form, leverage: Number(e.target.value) })} min="1" max="100" />
          </div>
          <div className="form-group">
            <label>Unrealized PnL ($)</label>
            <input type="number" value={form.unrealizedPnl} onChange={(e) => setForm({ ...form, unrealizedPnl: Number(e.target.value) })} step="1000" />
          </div>
          <div className="form-group">
            <label>ROE (decimal)</label>
            <input type="number" value={form.returnOnEquity} onChange={(e) => setForm({ ...form, returnOnEquity: Number(e.target.value) })} step="0.01" />
          </div>
        </div>
        <button onClick={() => handleOpenPosition()} className="debug-btn simulate-btn open">
          Open Position
        </button>
      </div>

      {/* Actions */}
      <div className="debug-section">
        <h4>Actions</h4>
        <div className="button-grid">
          <button onClick={handleUpdateLastPnl} className="debug-btn action-btn update" disabled={simulatedRef.current.length === 0}>
            Update PnL (last)
          </button>
          <button onClick={handleCloseLastPosition} className="debug-btn action-btn close" disabled={simulatedRef.current.length === 0}>
            Close Last
          </button>
          <button onClick={handleCloseAllSimulated} className="debug-btn action-btn close-all" disabled={simulatedRef.current.length === 0}>
            Close All Simulated
          </button>
        </div>
      </div>
    </div>
  );
}

export default PositionDebugPanel;
